<template>
  <div>
    <MapView @forestToTree="forestToTreeAnim" ref="mapPane"/>
    <TreeView @treeToForest="treeToForestAnim" @treeToSky="treeToSkyAnim" ref="treePane"/>
    <SkyView @skyToTree="skyToTreeAnim" ref="skyPane"/>
    <div id="fixedUI">
      <div class="mb-1">
        <b-button variant="dark" @click="backFormPage">
          <img class="uiIcon" src="@/assets/img/map/pencil.png">
          <p class="buttonText">SUBMIT STORY</p>
        </b-button>
      </div>
      <div class="mb-1">
        <b-button variant="dark" @click="backMainPage">
          <img class="uiIcon" src="@/assets/img/map/sitelogo.png">
          <p class="buttonText">PURPOSE CENTRE</p>
        </b-button>
      </div>
      <div>
        <b-button variant="dark" @click="playAudio">
          <img id="soundButton" class="uiIcon" src="@/assets/img/map/mute.png">
          <p id="soundText" class="buttonText">MUTE SOUND</p>
        </b-button>
      </div>
    </div>
    <div id="screenWrap">
    </div>
    <div id="introBox">
      <p>Welcome to Temasek's <span class='text-info'><b>Forest of Purpose</b></span>. Here we collect stories from across Temasek that reflect our Purpose Statement - "So Every Generation Prospers".</p>
      <p>You can explore the stories of purpose that your colleagues have submitted to grow their team's trees.</p>
      <p><i>Click to get started.</i></p>
      <div class="text-center">
        <img src="@/assets/img/map/play-button.png" width="80px" @click="startApp">
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3"
import { mapActions } from 'vuex'
import gsap from "gsap"
import { Howl } from 'howler'

import MapView from './components/MapView.vue'
import TreeView from './components/TreeView.vue'
import SkyView from './components/SkyView.vue'

export default {
  name: 'App',
  metaInfo: {
    title: 'Temasek Purpose Tree',
    htmlAttrs: {
      lang: 'en-US'
    },
    meta: [
      { charset: 'utf-8' },
      { name: 'description', content: 'Temasek Purpose Tree' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no' }
    ]
  },  
  components: {
    MapView,
    TreeView,
    SkyView
  },
  data: () => ({
    treeToSky: gsap.timeline(),
    treeToMap: gsap.timeline(),
    mapAnim: gsap.timeline(),
    treeAnim: gsap.timeline(),
    balloonAnim: gsap.timeline(),
    sound: new Howl({
        src: ["sound/nature.mp3"],
        html5: true,
        loop: true,
        volume: 0.7
      }),
    playSound: false,
    butterflies: 0,
    butterflyArray: []
  }),
  methods: {
    ...mapActions([
      "loadData"
    ]),
    addButterflies: function() {
      let width = window.innerWidth;

      this.butterflies = Math.floor(width / 120);
    
      d3.select("#butterflyLayer img").remove();
      for(let i=0; i < this.butterflies; i++) {
        let newObject = {};

        let img = Math.floor(Math.random() * 3);
        if (img == 0) {
          newObject.color = "blue";
        } else if (img == 1) {
          newObject.color = "orange";
        } else {
          newObject.color = "pink";
        }      

        newObject.x = Math.random() * width;
        newObject.y = Math.random() * 100;

        d3.select("#butterflyLayer").append("img")
          .attr("id", "butterfly" + i)
          .style("position", "absolute")
          .style("left", newObject.x + "px")
          .style("top", newObject.y + "px");

        this.butterflyArray.push(newObject);
      }
    },
    initializeAnimations: function() {
      let height = window.innerHeight;
      let skyHeight = height;

      let width = window.innerWidth;
      if (width < 800) { skyHeight = height * 0.75 }

      // Tree to Sky animation
      this.treeToSky.staggerTo("div.backgroundFrame .background", 3, {y: skyHeight, ease: "power2.inOut" }, 0.05, 0);
      this.treeToSky.staggerTo("div.midgroundFrame .midground", 2.8, {y: skyHeight, ease: "power2.inOut" }, 0.035, 0.15);
      this.treeToSky.to("div#treeLayer", 3, {y: skyHeight, ease: "power2.inOut" }, 0.2);
      this.treeToSky.to("div#wishLayer", 3, {y: skyHeight, ease: "power2.inOut" }, 0.2);
      this.treeToSky.staggerTo("div.foregroundFrame .foreground", 2, {y: skyHeight, autoAlpha:0, ease: "power2.inOut" }, 0.1, 0);
      this.treeToSky.staggerTo("div.foregroundFrame .foreground2", 2, {y: skyHeight, autoAlpha:0, ease: "power2.inOut" }, 0.1, 0.6);

      this.treeToSky.to("#mapLogo", 1, {autoAlpha: 0}, 0);
      this.treeToSky.from("#skyView", 2, {autoAlpha: 0}, 1);

      this.treeToSky.to("#callToActionLayer", 2, {autoAlpha: 0}, 0);
      this.treeToSky.to("#signboardLayer", 2, {autoAlpha: 0}, 0);
      this.treeToSky.to("#groupLabel", 2, {autoAlpha: 0}, 0);

      this.treeToSky.to("#butterflyLayer", 3, {y: skyHeight, ease:"power2.inOut"}, 0.2);
      this.treeToSky.to("#balloonLayer", 3, {y: skyHeight, ease:"power2.inOut"}, 0.2);

      this.treeToSky.pause();

      // Tree to Map animation
      this.treeToMap.staggerTo("div.backgroundFrame .background", 3, {y: -height, autoAlpha:0, ease: "power2.inOut" }, 0.05, 0);
      this.treeToMap.staggerTo("div.midgroundFrame .midground", 2.8, {y: -height, autoAlpha:0, ease: "power2.inOut" }, 0.035, 0.15);
      this.treeToMap.to("div#treeLayer", 3, {y: -height, autoAlpha:0, ease: "power2.inOut" }, 0.2);
      this.treeToMap.to("div#wishLayer", 3, {y: -height, autoAlpha:0, ease: "power2.inOut" }, 0.2);
      this.treeToMap.staggerTo("div.foregroundFrame .foreground", 2, {y: height, autoAlpha:0, ease: "power2.inOut" }, 0.1, 0);
      this.treeToMap.staggerTo("div.foregroundFrame .foreground2", 2, {y: height, autoAlpha:0, ease: "power2.inOut" }, 0.1, 1);
      
      this.treeToMap.to("#mapLogo", 1, {autoAlpha: 0}, 0);
      this.treeToMap.from("#mapView", 3, {autoAlpha: 0}, 0);

      this.treeToMap.to("#signboardLayer", 2, {autoAlpha: 0}, 0);
      this.treeToMap.to("#groupLabel", 2, {autoAlpha: 0}, 0);
      this.treeToMap.to("#callToActionLayer", 2, {autoAlpha: 0}, 0);

      this.treeToMap.to("#butterflyLayer", 3, {y: -height, autoAlpha:0, ease:"power2.inOut"}, 0.2);
      this.treeToMap.to("#balloonLayer", 3, {y: -height, autoAlpha:0, ease:"power2.inOut"}, 0.2);
      
      this.treeToMap.pause();
      this.treeToMap.progress(1, false);

      //Map animations
      this.mapAnim.to("#cloud1", 30,  {x: width + 500, ease:"none", repeat: -1}, 0);
      this.mapAnim.to("#cloud2", 30,  {x: width + 500, ease:"none", repeat: -1}, 15);
      this.mapAnim.to("#cloud3", 30,  {x: width + 500, ease:"none", repeat: -1}, 7);
      this.mapAnim.to("#cloud4", 30,  {x: width + 500, ease:"none", repeat: -1}, 23);

      this.mapAnim.to("#boat1", 45,  {x: width + 97, ease:"none", repeat: -1}, 0);
      this.mapAnim.to("#boat2", 45,  {x: width + 97, ease:"none", repeat: -1}, 25);

      //Tree animations - butterflies
      this.addButterflies();

      for(let i=0; i < this.butterflies; i++) {
        this.treeAnim.to("#butterfly" + i, gsap.utils.random(5, 10, 1), {
            x: ()=> { let dist = gsap.utils.random(-200, 200);
              d3.select("#butterfly" + i)
              .attr("src", () => {
                if (dist < 0) {
                  return require("@/assets/img/butterfly_" + this.butterflyArray[i].color + "_left.gif");
                } else {
                  return require("@/assets/img/butterfly_" + this.butterflyArray[i].color + "_right.gif");
                }
              })
              .attr("width", "40px");
              return dist; }, 
            y: ()=> { let dist = gsap.utils.random(-200, 200); return dist; }, 
            ease:"none", repeat: -1, repeatDelay: 1,
            repeatRefresh: true 
        }, 0);
      }
      this.treeAnim.pause();

      //Balloon animations - Tree OR Sky Pane

      this.balloonAnim.to("#balloon1", 40,  {x: width + 50, ease:"none", repeat: -1}, 0);
      this.balloonAnim.to("#balloon2", 40,  {x: width + 50, ease:"none", repeat: -1}, 10);
      this.balloonAnim.to("#balloon3", 40,  {x: width + 50, ease:"none", repeat: -1}, 15);
      this.balloonAnim.to("#balloon4", 40,  {x: width + 50, ease:"none", repeat: -1}, 20);

      this.balloonAnim.pause();
    },
    treeToSkyAnim: function(index) {
      this.treeToSky.play();

      setTimeout(() => {
        this.treeAnim.pause();
      }, 3000);

      this.$refs.skyPane.personIndex = index;
      d3.select("#textBox").property('scrollTop', 0)
    },
    skyToTreeAnim: function() {
      this.treeToSky.reverse();
      this.treeAnim.play();
    },
    treeToForestAnim: function() {
      this.treeToMap.play();
      this.mapAnim.play();

      setTimeout(() => {
        this.treeAnim.pause();
        this.balloonAnim.pause();
      }, 3000);

      this.$refs.mapPane.treeIndex = -1;
    },
    forestToTreeAnim: function(d) {
      this.treeToMap.reverse();
      this.treeAnim.play();
      this.balloonAnim.play();

      setTimeout(() => {
        this.mapAnim.pause();
      }, 3000);

      this.$refs.treePane.drawTree(d.treeType);
      this.$refs.treePane.drawWishLayer();
      this.$refs.treePane.layoutScene();
    },
    playAudio: function() {
      if (this.playSound) {
        this.playSound = false;
        this.sound.pause();
        d3.select("#soundButton")
          .attr("src", require("@/assets/img/map/sound.png"));
        d3.select("#soundText").text("PLAY SOUND");
      } else {
        this.playSound = true;
        this.sound.play();
        d3.select("#soundButton")
          .attr("src", require("@/assets/img/map/mute.png"));
        d3.select("#soundText").text("MUTE SOUND");
      }
    },
    startApp: function() {
      gsap.to("#screenWrap", 1, {autoAlpha:0}, 0);
      gsap.to("#introBox", 1, {autoAlpha:0}, 0);
      this.playAudio();
    },
    backMainPage: function() {
      window.open("https://tintl.sharepoint.com/sites/Purpose/");
    },
    backFormPage: function() {
      window.open("https://forms.office.com/pages/responsepage.aspx?id=8gA5CPMgmES8rEHv1JwEW_Po6hLePMZCp4W_8W2aCKpUOU5KT0pJNkRTMkNOVUFWQU01NENQT01INSQlQCN0PWcu");
    }
  },
  mounted: function () {
    this.loadData();
    this.initializeAnimations();
  },
}
</script>

<style>
body {
    overflow: hidden;
    background: #638540;
    margin: auto 0;
    width: 100vw;
    height: 100vh;
}

#fixedUI {
  position: fixed;
  bottom: 0.5%;
  right: 0.5%;
  padding: 0px;
}

#fixedUI .buttonText{
  font-size: 8px;
  line-height: 10px;
  margin-bottom: 0px;
  max-width: 40px;
}

.uiIcon {
  width: 40px;
  height: 40px;
}

#screenWrap {
  width: 100vw;
  height: 100vh;
  background: black;
  opacity: 0.7;
}

#introBox {
  position: absolute;
  width: 400px;
  height: 360px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 15px;
  opacity: 09;
  padding: 20px;
}

@media only screen and (max-width: 800px) {
  .uiIcon {
    width: 24px;
    height: 24px;
  }

  #fixedUI .buttonText{
    font-size: 6px;
    line-height: 7px;
    max-width: 24px;
  }
}

</style>
